import React, { useEffect, useState } from "react";
import UnderwritingInfoCard from "./UnderwritingInfoCard";
import Offer from "./Offer";
import { Row, Col, Container } from "react-bootstrap";
import PropTypes from 'prop-types';
import { getSessionStorageItem, SESSION_STORAGE_ACCOUNT_KEY, SESSION_STORAGE_AMERICO_DECLINED_REASONS_KEY } from "../../../../sessionStorage";

export default function UnderWritingOffer(props) {
    const { formState, basicInfo, offerOnFieldChange, onMultiFieldChange } = props;

    const covrServicesUrl = window.__ENV__.REACT_APP_COVR_SERVICES_URL;

    const WAIT_SECONDS = 10; // how many seconds between each status check
    const FIRST_WAITING_ATTEMPTS_COUNT = 12; // how many attempts before showing the [Notify Me] button (12*10 == 120 seconds == 2 minutes)
    const END_WAITING_ATTEMPTS_COUNT = 30; // how many attempts before ending on "We will notify you via email" (30*10 = 300 seconds == 5 minutes)

    const statuses = {
        WAITING: "WAITING",
        APPROVED: "APPROVED",
        APPROVEDMODIFIED: "APPROVEDMODIFIED",
        LONGWAIT: "LONGWAIT",
        TOOLONG: "TOOLONG",
        DECLINED: "DECLINED",
    }

    const [currentStatus, setCurrentStatus] = useState(statuses.WAITING);
    const [attempts, setAttempts] = useState(1);
    const [notifyMe, setNotifyMe] = useState(false);

    const getUnderwritingStatus = () => {
        return new Promise(resolve => {
            fetch(`${covrServicesUrl}/api/Americo/UnderwritingStatus?PolicyNumber=${props.formState.policyNumber}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'SessionId': getSessionStorageItem(SESSION_STORAGE_ACCOUNT_KEY).sessionId,
                },
            }).then(res => res.json())
                .then(json => {
                    var parsedStatus;

                    switch (json.decision) {
                        case "01": // Approved
                            parsedStatus = statuses.APPROVED;
                            break;
                        case "06": // Approved or Approved Modified
                            parsedStatus = statuses.APPROVEDMODIFIED;
                            break;
                        case "03": // Declined
                        case "05": // No Decision / Unable to Make Decision
                            parsedStatus = statuses.DECLINED;
                            break;
                        default:
                            parsedStatus = statuses.WAITING;
                            break;
                    }

                    var res = {
                        status: parsedStatus,
                        response: json,
                    }

                    resolve(res);
                });
        });
    };

    const PremiumClassMappings = [
        {
            PremiumClass: '27',
            Tobacco: false,
            Health: 'best',
        },
        {
            PremiumClass: '28',
            Tobacco: true,
            Health: 'best',
        },
        {
            PremiumClass: '29',
            Tobacco: false,
            Health: 'better',
        },
        {
            PremiumClass: '30',
            Tobacco: true,
            Health: 'better',
        },
        {
            PremiumClass: '31',
            Tobacco: false,
            Health: 'good',
        },
        {
            PremiumClass: '32',
            Tobacco: true,
            Health: 'good',
        }
    ];

    const DecisionReasonMessages = [
        { Code: "240", Message: "Moving Violations" },
        { Code: "116", Message: "Driving Record" },
        { Code: "242", Message: "Admitted Nicotine Use" },
        { Code: "TBD", Message: "Approved at better class than applied" }, // ----- TBD -----
        { Code: "243", Message: "Admitted Marijuana Use" },
        { Code: "245", Message: "Medical History" },
        { Code: "171", Message: "Build provided falls outside the acceptable guidelines for this product" },
        { Code: "83", Message: "Prior Americo policy with Nicotine or smoker class" },
        { Code: "170", Message: "Prescription Check" },
        { Code: "173", Message: "True Risk Life Score" },
        { Code: "07", Message: "Build provided falls outside the acceptable guidelines for this product" },
        { Code: "85", Message: "Internal Replacements not allowed for this product" },
        { Code: "199", Message: "Information received from prior application" },
        { Code: "237", Message: "Information received from prior application" },
        { Code: "235", Message: "Confidential Information from MIB" },
        { Code: "236", Message: "Multiple reasons including: Prescription Check, Driving Record, and/or Confidential Information from MIB" },
        { Code: "172", Message: "True Risk Life Score" },
        { Code: "214", Message: "Medical History" },
        { Code: "244", Message: "Medical History" },
        { Code: "17", Message: "Occupation" },
        { Code: "239", Message: "Ineligible for Additional Insurance" },
        { Code: "241", Message: "Unable to Obtain True Risk Life Score due to status of Client's TransUnion account" },
        { Code: "246", Message: "Unable to obtain information from Milliman" },
        { Code: "238", Message: "Aviation Exclusion will apply" },
        { Code: "247", Message: "Unable to Obtain True Risk Life Score" },
    ];

    // when it's approved, update healthClass and tobacco using the returned PremiumClass
    const onApproved = (underwritingStatusRes) => {
        var changedFields = [];

        const appliedHealthClass = props.formState.steps.quoter.healthClass;
        const appliedNicotine = props.formState.steps.basicInfo.tobacco;
        const { decisionReasons } = underwritingStatusRes.response;
        const approvedModifiedFormState = { ...props.formState };

        if (underwritingStatusRes.status === statuses.APPROVEDMODIFIED) {
            if (underwritingStatusRes.response.premiumClass) {
                var premiumClass = PremiumClassMappings.find(p => p.PremiumClass === underwritingStatusRes.response.premiumClass);
                changedFields.push('premium');

                if (appliedHealthClass !== premiumClass.Health) {
                    approvedModifiedFormState.steps.quoter.healthClass = premiumClass.Health;
                    changedFields.push('healthClass');
                }

                if (appliedNicotine !== premiumClass.Tobacco.toString()) {
                    approvedModifiedFormState.steps.basicInfo.tobacco = premiumClass.Tobacco;
                    changedFields.push('tobacco');
                }

                if (premiumClass.Health === 'best' && premiumClass.Tobacco && decisionReasons && decisionReasons.indexOf("83") > -1) {
                    approvedModifiedFormState.steps.medicalHistory.nicotineUse = "Within the last year";
                    changedFields.push('nicotineUse');
                }

                if (premiumClass.Health === 'best' && premiumClass.Tobacco && decisionReasons && decisionReasons.indexOf("170") > -1
                    && (appliedHealthClass === 'best' || appliedHealthClass === 'better' || appliedHealthClass === 'good') && appliedNicotine === "false") {
                    approvedModifiedFormState.steps.medicalHistory.nicotineUse = "Within the last year";
                    changedFields.push('nicotineUse');
                }

                if (premiumClass.Health === 'better' && premiumClass.Tobacco === false && decisionReasons && decisionReasons.indexOf("83") > -1) {
                    approvedModifiedFormState.steps.medicalHistory.nicotineUse = "Within the last 13-36 months";
                    changedFields.push('nicotineUse');
                }

                if (premiumClass.Health === 'better' && premiumClass.Tobacco && decisionReasons && decisionReasons.indexOf("170") > -1
                    && (appliedHealthClass === 'best' || appliedHealthClass === 'better' || appliedHealthClass === 'good') && appliedNicotine === "false") {
                    approvedModifiedFormState.steps.medicalHistory.useRecreationalMarijuana = "true";
                    changedFields.push('useRecreationalMarijuana');
                    approvedModifiedFormState.steps.medicalHistory.marijuanaFrequency = "Once a week";
                    changedFields.push('marijuanaFrequency');
                }
            }

            if (decisionReasons && decisionReasons.indexOf("238") > -1) {
                approvedModifiedFormState.steps.personalHistory.flights = "true";
            }
        }
        approvedModifiedFormState.decisionResponse = underwritingStatusRes;
        approvedModifiedFormState.fieldsChangedAfterUnderwriting = changedFields;

        props.onDecision(approvedModifiedFormState);
    };

    const run = () => {
        getUnderwritingStatus().then(underwritingStatusRes => {
            setCurrentStatus(underwritingStatusRes.status);
            switch (underwritingStatusRes.status) {
                case (statuses.APPROVED):
                case (statuses.APPROVEDMODIFIED):
                    onApproved(underwritingStatusRes);
                    break;
                case (statuses.WAITING):
                    waiting();
                    break;
                case (statuses.DECLINED):
                default:
                    // filter out codes we don't have mapped, then return the Message(s)
                    const decisionMessages = DecisionReasonMessages.filter(dcm => underwritingStatusRes.response.decisionReasons.find(r => r === dcm.Code)).map(dcm => dcm.Message);

                    // -----  for development, until Americo gives us the TBDs - -----
                    const unmappedCodes = underwritingStatusRes.response.decisionReasons.filter(r => DecisionReasonMessages.findIndex(dcm => dcm.Code === r) < 0);
                    if (unmappedCodes.length) {
                        console.error("Unmapped Americo Underwriting Decision Reasons: ", unmappedCodes);
                    }
                    // ----- end -----

                    sessionStorage.setItem(SESSION_STORAGE_AMERICO_DECLINED_REASONS_KEY, JSON.stringify(decisionMessages));

                    props.onDeclined(decisionMessages);
                    break;
            }
        });
    }

    const waiting = () => {
        if (attempts >= FIRST_WAITING_ATTEMPTS_COUNT && attempts < END_WAITING_ATTEMPTS_COUNT) {
            setCurrentStatus(statuses.LONGWAIT);
        }

        if (attempts >= END_WAITING_ATTEMPTS_COUNT) {
            setCurrentStatus(statuses.TOOLONG);
            notify(); //incase the response comes back between now and the ticket job
            return;
        }

        // run again in 30 seconds
        setTimeout(() => setAttempts(attempts + 1), 1000 * WAIT_SECONDS); // in x seconds, increment `attempts` to trigger the useEffect below
    }

    useEffect(() => {
        // Check the api for status on the first load and whenever attempts is incremented
        if (!notifyMe) {
            run();
        }
    }, [attempts]);

    const notify = () => {
        fetch(`${covrServicesUrl}/api/ConsumerApplication/NotifyMe`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'SessionId': getSessionStorageItem(SESSION_STORAGE_ACCOUNT_KEY).sessionId,
                'X-Consumer-Application-ID': getSessionStorageItem(SESSION_STORAGE_ACCOUNT_KEY).consumerApplicationId,
            },
        });
        setCurrentStatus(statuses.TOOLONG);
        setNotifyMe(true);
    }
    return (
        <Container>
            <Row className='formPage justify-content-center underwriting-offer-div'>
                <Col md={6} className="d-flex justify-content-center w-100">
                    <UnderwritingInfoCard status={currentStatus} statuses={statuses} quoter={props.formState.steps.quoter} />
                </Col>
                <Col md={6} className="d-flex justify-content-center w-100">
                    <Offer
                        quoter={formState.steps.quoter}
                        onFieldChange={offerOnFieldChange}
                        basicInfo={basicInfo}
                        status={currentStatus}
                        statuses={statuses}
                        fullFormState={formState}
                        onMultiFieldChange={onMultiFieldChange}
                        onNext={props.onNext}
                        handleApprovedPremiumUpdate={props.handleApprovedPremiumUpdate}
                        onCoverageChanged={props.onCoverageChanged}
                    />
                </Col>
            </Row>
        </Container>
    );
}

UnderWritingOffer.propTypes = {
    formState: PropTypes.object.isRequired,
    onNext: PropTypes.func.isRequired,
    onDeclined: PropTypes.func.isRequired,
    onDecision: PropTypes.func,
    onMultiFieldChange: PropTypes.func,
    basicInfo: PropTypes.object,
    offerOnFieldChange: PropTypes.func,
    onCoverageChanged: PropTypes.func,
};
